import React, { useEffect, useState } from 'react';

import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Grid } from '@wds/grid';
import {
	IconCheckCircle,
	IconError,
	IconEye,
	IconXCircle,
} from '@wds/icons';
import { Input } from '@wds/input';
import { InputGroup, InputGroupAddon } from '@wds/input-group';
import { InputMessage } from '@wds/input-message';
import { Popout } from '@wds/popout';
import { Typography } from '@wds/typography';

import PropTypes from 'prop-types';
import STRINGS from '../../../constants';
import PasswordField from '../../../../sma.accounts/react/components/PasswordField';

const EditableTextField = (props) => {
	const {
		id,
		fields,
		label,
		editable,
		state,
		setState,
		reset,
		editingFields,
		setEditingFields,
	} = props;
	const [tempState, setTempState] = useState({});
	const [editing, setEditing] = useState(false);
	const [displayText, setDisplayText] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [fieldErrors, setFieldErrors] = useState({});
	const [originalState, setOriginalState] = useState({});
	const [changed, setChanged] = useState(false);
	const [isPasswordValid, setIsPasswordValid] = useState(false);
	const [disableSave, setDisableSave] = useState(true);

	useEffect(() => {
		if (Object.keys(originalState).length === 0) {
			handleSetOriginalState();
		}
		resetState();
	}, [fields]);

	useEffect(() => {
		if (editing) {
			const newEditingFields = { ...editingFields, [id]: true };
			setEditingFields(newEditingFields);
		} else {
			const editingFieldsCopy = { ...editingFields };
			delete editingFieldsCopy[id];
			setEditingFields(editingFieldsCopy);
		}
	}, [editing]);

	useEffect(() => {
		if (reset) {
			handleSetOriginalState();
			setChanged(false);
			setEditing(false);
		}
	}, [reset]);

	useEffect(() => {
		let unlockSubmit = true;
		if (JSON.stringify(tempState) !== JSON.stringify(originalState)) {
			setChanged(true);
			fields.forEach((field) => {
				const isFieldError = fieldErrors[field.id];
				if (isFieldError || (field.id === 'new_password' && !isPasswordValid)) {
					unlockSubmit = false;
				}
			});
		} else {
			unlockSubmit = false;
		}
		setDisableSave(!unlockSubmit);
	}, [tempState]);

	const handleStateChange = (e) => {
		const { id: fieldId, value } = e.target;
		setTempState({ ...tempState, [fieldId]: value });
	};

	const handlePasswordChange = (e) => {
		const newState = { ...tempState };
		newState[e.target.id] = e.target.value;
		setTempState(newState);
	};

	const togglePasswordVisibility = () => {
		setShowPassword(!showPassword);
	};

	const isValidEmail = (email) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	const getInputType = (field) => {
		if (field.type === 'password') {
			return showPassword ? 'text' : 'password';
		}
		return field.type;
	};

	const resetState = () => {
		let newDisplayText = '';
		let newTempState = {};
		fields.forEach((field) => {
			newTempState = { ...newTempState, [field.id]: state[field.id] };
			if (field.type === 'password') {
				newDisplayText = '';
			} else {
				newDisplayText += `${field.value}`;
				if (field !== fields[fields.length - 1]) {
					newDisplayText += ' ';
				}
			}
		});
		setTempState(newTempState);
		setDisplayText(newDisplayText);
		setChanged(false);
	};

	const validateFields = () => {
		const fieldErrorsObj = {};
		fields.forEach((field) => {
			if (field.type === 'email' && !isValidEmail(tempState[field.id])) {
				fieldErrorsObj[field.id] = true;
			}
			if (field.isRequired && !tempState[field.id]) {
				fieldErrorsObj[field.id] = !!(field.isRequired && !tempState[field.id]);
			}
		});
		setFieldErrors(fieldErrorsObj);
		return fieldErrorsObj;
	};

	const handleUpdate = () => {
		const fieldErrorsObj = validateFields();
		if (Object.values(fieldErrorsObj).some((error) => error)) {
			return;
		}
		setState({
			...state,
			...tempState,
		});
		setEditing(false);
	};

	const handleCancel = () => {
		let tempStateCopy = {};
		fields.forEach((field) => {
			tempStateCopy = { ...tempStateCopy, [field.id]: state[field.id] };
		});
		setTempState(tempStateCopy);
		resetState();
		setEditing(false);
	};

	const handleClear = () => {
		setState({
			...state,
			...originalState,
		});
		setTempState(originalState);
		resetState();
		setChanged(false);
	};

	const handleSetOriginalState = () => {
		let originalFields = {};
		fields.forEach((field) => {
			originalFields = { ...originalFields, [field.id]: field.value };
		});
		setOriginalState(originalFields);
		setTempState(originalState);
	};

	return (
		<Box id={`${id} container`} role='region' aria-labelledby={`${id}-label`}>
			<Box display='flex' flexDirection='column' gap={2}>
				<Box display='flex' gap={2}>
					<Box display='flex' justifyContent='center' gap={2}>
						<Typography
							id={`${id}-label`}
							variant='bodySm'
							weight='medium'
							aria-label={label}
						>
							{label}
						</Typography>
					</Box>
					{!editing && (
						<Box display='flex' justifyContent='center' gap={2}>
							<Box
								onClick={editable ? () => setEditing(true) : undefined}
								tabIndex='0'
								aria-label={STRINGS.labels.showChangeFields}
								role='button'
							>
								<Typography
									variant='bodySm'
									weight='medium'
									href={editable ? '#' : undefined}
									color={editable ? 'link' : 'text'}
									style={{ cursor: editable ? 'pointer' : 'not-allowed' }}
								>
									{STRINGS.change}
								</Typography>
							</Box>
							<Box display='flex' ml={4} justifyContent='space-between' gap={2}>
								{!reset && changed && (
									<>
										<Typography
											id={`${id}-unsaved-changes`}
											variant='bodySm'
											weight='medium'
											color='warning'
											aria-live='polite'
										>
											{STRINGS.unsavedChanges}
										</Typography>
										<Box display='flex' alignItems='center'>
											<IconXCircle
												size='sm'
												color='warning'
												onClick={handleClear}
												aria-label={STRINGS.labels.clearChanges}
											/>
										</Box>
									</>
								)}
							</Box>
						</Box>
					)}
				</Box>
				{!editing && (
					<Typography
						component='span'
						id={`sma-ef-${id}-display-text`}
						variant='bodySm'
						tabIndex='0'
						aria-label={`${id} ${STRINGS.labels.displayText}`}
					>
						{displayText}
					</Typography>
				)}
			</Box>
			<Box width='100%'>
				<Grid noBleeds>
					{fields.map((field) => (editing ? (
						<Grid.Item xs={fields.length < 2 ? 12 : 6} key={field.id}>
							<Box
								display='flex'
								justifyContent='left'
								gap={2}
								style={{ marginTop: '7px' }}
							>
								{(field.type !== 'password' || field.id === 'current_password') && (
									<InputGroup
										color={fieldErrors[field.id] ? 'danger' : 'default'}
										stretched
									>
										<Input
											size='md'
											placeholder={field.label}
											value={tempState[field.id]}
											id={field.id}
											type={getInputType(field)}
											color={fieldErrors[field.id] ? 'danger' : 'default'}
											onChange={handleStateChange}
											stretched
											aria-invalid={fieldErrors[field.id] || undefined}
											aria-describedby={
												fieldErrors[field.id] ? `${field.id}-error` : undefined
											}
										/>
										{field.type === 'password' && (
											<InputGroupAddon>
												<IconEye
													onClick={togglePasswordVisibility}
													aria-label={showPassword ? STRINGS.labels.hidePassword : STRINGS.labels.showPassword}
												/>
											</InputGroupAddon>
										)}
									</InputGroup>
								)}
								{field.id === 'new_password' && (
									<>
										<PasswordField
											id='new_password'
											onChange={handlePasswordChange}
											size='md'
											setIsPasswordValid={setIsPasswordValid}
											aria-required='true'
										/>
										{!isPasswordValid && fieldErrors?.password && (
											<InputMessage color='error' role='alert'>
												{fieldErrors[field.id] && (field.errorMessage)}
											</InputMessage>
										)}
									</>
								)}
								{fieldErrors[field.id] && (
									<Popout
										show={fieldErrors[field.id]}
										clickable
										trigger={(
											<IconError
												title='error'
												color='error'
												tabIndex={0}
												aria-label={STRINGS.labels.fieldError}
											/>
										)}
									>
										<Popout.Body id={`${field.id}-error`}>
											{field.errorMessage
												? field.errorMessage
												: STRINGS.fieldErrors.text}
										</Popout.Body>
									</Popout>
								)}
							</Box>
						</Grid.Item>
					) : undefined))}
				</Grid>
			</Box>
			{editing && (
				<Box display='flex' justifyContent='flex-end'>
					<Button
						color='warning'
						size='sm'
						variant='text'
						onClick={editable ? () => handleCancel() : undefined}
						aria-label={STRINGS.cancel}
					>
						<IconXCircle size='sm' color='danger' />
					</Button>
					<Button
						color='primary'
						size='sm'
						variant='text'
						onClick={editable ? () => handleUpdate() : undefined}
						aria-label={STRINGS.saveBtn}
						disabled={disableSave}
					>
						<IconCheckCircle size='sm' color='primary' />
					</Button>
				</Box>
			)}
		</Box>
	);
};

EditableTextField.propTypes = {
	id: PropTypes.string.isRequired,
	fields: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			type: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			isRequired: PropTypes.bool,
			errorMessage: PropTypes.string,
			value: PropTypes.string,
			autoComplete: PropTypes.string,
		}),
	).isRequired,
	label: PropTypes.string.isRequired,
	editable: PropTypes.bool,
	state: PropTypes.object.isRequired,
	setState: PropTypes.func.isRequired,
	reset: PropTypes.bool.isRequired,
	editingFields: PropTypes.object,
	setEditingFields: PropTypes.func,
};
EditableTextField.defaultProps = {
	editable: true,
	editingFields: {},
	setEditingFields: () => {
	},
};
export default EditableTextField;
